import { useMemo } from 'react';
import { useStatsigClient } from '@statsig/react-bindings';

import { DEFAULT_ONBOARDING_WELCOME_CONFIG, type OnboardingWelcomeConfig } from '../types';

const CONFIG_NAME = 'onboarding_welcome_config';

/**
 * Hook to get onboarding welcome step configuration from Statsig dynamic config.
 *
 * This allows the welcome step content (title, CTAs, feature list) to be
 * configured remotely without code changes.
 *
 * @returns OnboardingWelcomeConfig with dynamic values or defaults
 *
 * @example
 * const config = useOnboardingWelcomeConfig();
 * // Use config.title, config.primaryCta.label, etc.
 */
export function useOnboardingWelcomeConfig(): OnboardingWelcomeConfig {
  const { client } = useStatsigClient();

  return useMemo(() => {
    const dynamicConfig = client.getDynamicConfig(CONFIG_NAME);
    const configValue = dynamicConfig.value as Partial<OnboardingWelcomeConfig>;

    // Merge config values with defaults, ensuring all required fields are present
    return {
      title: configValue.title ?? DEFAULT_ONBOARDING_WELCOME_CONFIG.title,
      subtitle: configValue.subtitle ?? DEFAULT_ONBOARDING_WELCOME_CONFIG.subtitle,
      description: configValue.description ?? DEFAULT_ONBOARDING_WELCOME_CONFIG.description,
      primaryCta: {
        ...DEFAULT_ONBOARDING_WELCOME_CONFIG.primaryCta,
        ...configValue.primaryCta,
      },
      secondaryCta: {
        ...DEFAULT_ONBOARDING_WELCOME_CONFIG.secondaryCta,
        ...configValue.secondaryCta,
      },
      featureList: {
        ...DEFAULT_ONBOARDING_WELCOME_CONFIG.featureList,
        ...configValue.featureList,
      },
    };
  }, [client]);
}
